import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import client from '../API/API';
import { SkeletonTableRow } from '../components/ui/Skeleton';
import { Icon, ICONS } from '../components/ui/Icon';

// ── Helpers ───────────────────────────────────────────────────────────────────
const toYMD = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const today = () => toYMD(new Date());
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return toYMD(d); };

const flatChannels = (record) => [
  ...(record.digital_inputs  || []),
  ...(record.digital_outputs || []),
  ...(record.analog_inputs   || []),
  ...(record.analog_outputs  || []),
  ...(record.other           || []),
];

const SearchIcon   = ICONS.search;
const DownloadIcon = ICONS.download;
const ChevronL     = ICONS.chevronRight; // rotated via transform if needed
const ChevronR     = 'M9 18l6-6-6-6';

const PAGE_SIZE = 50;

const fmtVal = (v) => {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, s.lastIndexOf(':'));
  const n = parseFloat(s);
  if (!isNaN(n) && s !== '') {
    const c = Math.max(0, n);
    return Number.isInteger(c) ? String(c) : c.toFixed(2);
  }
  return s;
};

// ── Preset chip ───────────────────────────────────────────────────────────────
function PresetChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: 20,
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-subtle)'}`,
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--ink-500)',
        fontFamily: 'var(--font-sans)', fontSize: 12,
        cursor: 'pointer', transition: 'all var(--t-fast)',
      }}
    >
      {label}
    </button>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function TrailPage() {
  const [machines,   setMachines]   = useState([]);
  const [machineId,  setMachineId]  = useState('');
  const [dateValue,  setDateValue]  = useState(today());
  const [trails,     setTrails]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [searched,   setSearched]   = useState(false);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows,  setTotalRows]  = useState(0);
  const [activePreset, setActivePreset] = useState('today');

  useEffect(() => {
    client.get('/api/machines/')
      .then(r => setMachines(r.data.machines || []))
      .catch(console.error);
  }, []);

  const handleSearch = async (nextPage = 1) => {
    if (!machineId) return;
    setLoading(true);
    try {
      const res = await client.get('/api/trails/', {
        params: { machine_id: machineId, date: dateValue, page: nextPage, page_size: PAGE_SIZE },
      });
      setTrails(res.data.trails || []);
      setPage(res.data.page || nextPage);
      setTotalPages(res.data.total_pages || 1);
      setTotalRows(res.data.total_records || 0);
      setSearched(true);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    if (!machineId) return;
    const token = localStorage.getItem('token');
    const res   = await fetch(`/api/trails/export/?machine_id=${machineId}&date=${dateValue}`,
      { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: `trails_${machineId}_${dateValue}.xlsx` });
    a.click(); URL.revokeObjectURL(url);
  };

  const setPreset = (preset) => {
    setActivePreset(preset);
    setDateValue(preset === 'today' ? today() : yesterday());
  };

  const colHeaders = trails.length > 0 ? flatChannels(trails[0]).map(ch => ch.name) : [];
  const start      = (page - 1) * PAGE_SIZE + 1;
  const end        = Math.min(page * PAGE_SIZE, totalRows);

  return (
    <div style={{ maxWidth: 1640, margin: '0 auto' }}>

      {/* ── Page head ── */}
      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
        style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 25, letterSpacing: '-0.5px', color: 'var(--ink-900)', marginBottom: 4 }}>
          Trail
        </h1>
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>
          Per-minute machine data log
        </p>
      </motion.div>

      {/* ── Filter bar ── */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.05 }}
        style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border-hair)',
          borderRadius: 'var(--r-lg)', padding: '16px 18px', marginBottom: 16,
          boxShadow: 'var(--sh-sm)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          {/* Machine select */}
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Machine
            </label>
            <select
              value={machineId}
              onChange={e => setMachineId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select machine…</option>
              {machines.map(m => (
                <option key={m.machine_id} value={m.machine_id}>
                  {m.machine_name || m.machine_id}
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Date
            </label>
            <input
              type="date"
              value={dateValue}
              max={today()}
              onChange={e => { setDateValue(e.target.value); setActivePreset(''); }}
              style={{ ...selectStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          </div>

          {/* Search */}
          <button
            onClick={() => handleSearch(1)}
            disabled={!machineId || loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: machineId ? 'var(--accent)' : 'var(--border-strong)',
              color: '#fff', border: 'none',
              borderRadius: 'var(--r-md)', padding: '9px 18px',
              fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13,
              cursor: machineId ? 'pointer' : 'not-allowed',
              transition: 'background var(--t-fast)', alignSelf: 'flex-end',
            }}
            onMouseEnter={e => { if (machineId) e.currentTarget.style.background = 'var(--accent-hover)'; }}
            onMouseLeave={e => { if (machineId) e.currentTarget.style.background = 'var(--accent)'; }}
          >
            <Icon d={SearchIcon} size={14} />
            {loading ? 'Loading…' : 'Search'}
          </button>

          {/* Export */}
          <button
            onClick={handleExport}
            disabled={!searched || trails.length === 0}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent',
              color: searched ? 'var(--online)' : 'var(--ink-300)',
              border: `1px solid ${searched ? 'var(--online-border)' : 'var(--border-hair)'}`,
              borderRadius: 'var(--r-md)', padding: '9px 16px',
              fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13,
              cursor: searched ? 'pointer' : 'not-allowed',
              transition: 'all var(--t-fast)', alignSelf: 'flex-end', marginLeft: 'auto',
            }}
          >
            <Icon d={DownloadIcon} size={14} color={searched ? 'var(--online)' : 'var(--ink-300)'} />
            Export Excel
          </button>
        </div>

        {/* Quick presets */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <PresetChip label="Today"     active={activePreset === 'today'}     onClick={() => setPreset('today')} />
          <PresetChip label="Yesterday" active={activePreset === 'yesterday'} onClick={() => setPreset('yesterday')} />
        </div>
      </motion.div>

      {/* ── Results bar ── */}
      {searched && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 10, fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)',
        }}>
          <span>
            {totalRows > 0
              ? <>Showing <strong style={{ color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}>{start}–{end}</strong> of <strong style={{ color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}>{totalRows}</strong> records</>
              : 'No records found'
            }
          </span>
        </div>
      )}

      {/* ── Table ── */}
      {(searched || loading) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-hair)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--sh-sm)',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--bg-raised)', borderBottom: '2px solid var(--border-strong)' }}>
                  {/* Timestamp — sticky */}
                  <th style={{ ...thStyle, position: 'sticky', left: 0, background: 'var(--bg-raised)', zIndex: 2 }}>
                    Timestamp
                  </th>
                  {colHeaders.map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 8 }).map((_, i) => <SkeletonTableRow key={i} cols={Math.max(colHeaders.length + 1, 5)} />)
                  : trails.map((row, ri) => {
                      const channels = flatChannels(row);
                      const isEven   = ri % 2 === 0;
                      return (
                        <tr key={ri}
                          style={{ background: isEven ? 'var(--bg-surface)' : 'var(--bg-raised)', borderBottom: '1px solid var(--border-hair)' }}
                        >
                          {/* Timestamp — sticky */}
                          <td style={{
                            ...tdStyle,
                            position: 'sticky', left: 0, zIndex: 1,
                            background: isEven ? 'var(--bg-surface)' : 'var(--bg-raised)',
                            fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-500)',
                            whiteSpace: 'nowrap', width: 160,
                          }}>
                            {row.timestamp || '—'}
                          </td>
                          {channels.map((ch, ci) => (
                            <td key={ci} style={{
                              ...tdStyle,
                              fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-900)',
                              textAlign: typeof ch.value === 'number' ? 'right' : 'left',
                            }}>
                              {fmtVal(ch.value)}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (() => {
            // Sliding window: up to 7 page buttons centered on current page
            const WINDOW = 7;
            let start = Math.max(1, page - Math.floor(WINDOW / 2));
            let end   = Math.min(totalPages, start + WINDOW - 1);
            if (end - start < WINDOW - 1) start = Math.max(1, end - WINDOW + 1);
            const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
            const goTo = (pg) => { setPage(pg); handleSearch(pg); };
            return (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '14px 16px',
                borderTop: '1px solid var(--border-hair)',
              }}>
                <PagerBtn icon={ChevronL} disabled={page === 1} onClick={() => goTo(page - 1)} />
                {start > 1 && <>
                  <PageBtn pg={1} current={page} onClick={goTo} />
                  {start > 2 && <span style={{ color: 'var(--ink-300)', fontSize: 12 }}>…</span>}
                </>}
                {pages.map(pg => <PageBtn key={pg} pg={pg} current={page} onClick={goTo} />)}
                {end < totalPages && <>
                  {end < totalPages - 1 && <span style={{ color: 'var(--ink-300)', fontSize: 12 }}>…</span>}
                  <PageBtn pg={totalPages} current={page} onClick={goTo} />
                </>}
                <PagerBtn icon={ChevronR} disabled={page === totalPages} onClick={() => goTo(page + 1)} />
              </div>
            );
          })()}
        </motion.div>
      )}

      {/* No data */}
      {searched && !loading && trails.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ink-500)', fontFamily: 'var(--font-sans)', fontSize: 14 }}>
          No data found for the selected date.
        </div>
      )}
    </div>
  );
}

// ── Page number button ────────────────────────────────────────────────────────
function PageBtn({ pg, current, onClick }) {
  const active = pg === current;
  return (
    <button
      onClick={() => onClick(pg)}
      style={{
        width: 32, height: 32, borderRadius: 'var(--r-md)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-subtle)'}`,
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--ink-500)',
        fontFamily: 'var(--font-sans)', fontSize: 12, cursor: 'pointer',
        transition: 'all var(--t-fast)',
      }}
    >
      {pg}
    </button>
  );
}

// ── Pager button ──────────────────────────────────────────────────────────────
function PagerBtn({ icon, disabled, onClick }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 32, height: 32, borderRadius: 'var(--r-md)',
        border: '1px solid var(--border-subtle)',
        background: 'transparent',
        color: disabled ? 'var(--ink-100)' : 'var(--ink-500)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all var(--t-fast)',
      }}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d={icon} />
      </svg>
    </button>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const selectStyle = {
  padding: '8px 12px',
  fontFamily: 'var(--font-sans)', fontSize: 13,
  color: 'var(--ink-900)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--r-md)',
  outline: 'none', cursor: 'pointer',
  minWidth: 200,
};

const thStyle = {
  padding: '10px 14px',
  fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--ink-500)', textAlign: 'left', whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '11px 14px',
  fontFamily: 'var(--font-sans)', fontSize: 13,
};
